import type { MessageArtifact } from '@ideoz/core';

/**
 * Markdown to show when an artifact has no designed card.
 *
 * A chat summary is `format: "markdown"` and `content` is the markdown.
 * A user story also carries `structured_content`: a title plus a copy of
 * that markdown. History marks `format` as `"json"` because the title
 * object exists. On the live event, `content` is still that JSON string
 * and the markdown is only on `structuredContent.content`. After persist,
 * and after an edit, the row `content` is the markdown. Save patches only
 * that field, so the nested copy goes stale. Prefer the row when it is
 * plain markdown; use the nested copy only while `content` is still JSON.
 */
function isJsonObjectString(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{')) return false;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed);
  } catch {
    return false;
  }
}

export function genericArtifactMarkdown(
  artifact: Pick<MessageArtifact, 'content' | 'format' | 'structuredContent'>
): string | null {
  const row = artifact.content ?? '';
  if (row.trim() && !isJsonObjectString(row)) return row;

  const nested = artifact.structuredContent?.content;
  if (typeof nested === 'string' && nested.trim()) return nested;
  return null;
}
